import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CreditCard, Check, Wallet } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useStripePayment } from '@/hooks/useStripePayment';

interface CreditPackage {
  credits: number;
  price: number;
  bonus?: number;
  popular?: boolean;
}

const creditPackages: CreditPackage[] = [
  { credits: 100, price: 10 },
  { credits: 500, price: 45, bonus: 50, popular: true },
  { credits: 1000, price: 80, bonus: 120 },
  { credits: 2500, price: 200, bonus: 350 },
];

export const CreditTopUp: React.FC = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const { createPaymentIntent, processPayment: stripeProcessPayment, isCreatingIntent, isProcessingPayment } = useStripePayment();
  
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage>(creditPackages[1]);
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Get current credit balance
  const { data: creditWallet } = useQuery({
    queryKey: ['workspace-credits', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_credit_wallets')
        .select('*')
        .eq('workspace_id', currentWorkspace?.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id
  });

  // Simulate payment for demo (replace with actual Stripe integration later)
  const demoPayment = useMutation({
    mutationFn: async ({ credits, amount }: { credits: number; amount: number }) => {
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Call the topup function
      const { data, error } = await supabase.rpc('topup_workspace_credits', {
        workspace_id_param: currentWorkspace?.id,
        credit_amount: credits,
        description_param: `Demo credit top-up: ${credits} credits for $${amount}`
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setPaymentSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['workspace-credits'] });
      queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
      toast.success('Credits added successfully!');
    },
    onError: (error: any) => {
      toast.error(`Payment failed: ${error.message}`);
    }
  });

  const handlePayment = async () => {
    if (!currentWorkspace) return;

    setIsProcessing(true);
    const totalCredits = selectedPackage.credits + (selectedPackage.bonus || 0);
    
    try {
      // For demo, use the simulated payment
      await demoPayment.mutateAsync({
        credits: totalCredits,
        amount: selectedPackage.price
      });
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-success" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Payment Successful!</h3>
          <p className="text-muted-foreground mb-4">
            {selectedPackage.credits + (selectedPackage.bonus || 0)} credits have been added to your workspace.
          </p>
          <Button onClick={() => setPaymentSuccess(false)}>
            Purchase More Credits
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Top Up Credits</h2>
        <p className="text-muted-foreground">Choose a credit package to continue using BillPortal services</p>
        
        {/* Current Balance */}
        {creditWallet && (
          <div className="mt-4 p-4 bg-accent rounded-lg inline-flex items-center space-x-2">
            <Wallet className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Current Balance:</span>
            <Badge variant="secondary" className="font-semibold">
              {Math.floor(creditWallet.credits_balance)} credits
            </Badge>
          </div>
        )}
      </div>

      {/* Credit Packages */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {creditPackages.map((pkg) => (
          <Card 
            key={pkg.credits}
            className={`cursor-pointer transition-all ${
              selectedPackage === pkg 
                ? 'border-primary bg-primary/5' 
                : 'hover:border-muted-foreground/50'
            } ${pkg.popular ? 'ring-2 ring-primary/20' : ''}`}
            onClick={() => setSelectedPackage(pkg)}
          >
            <CardHeader className="text-center pb-2">
              {pkg.popular && (
                <Badge className="bg-primary text-primary-foreground mb-2 w-fit mx-auto">
                  Most Popular
                </Badge>
              )}
              <CardTitle className="text-lg">{pkg.credits} Credits</CardTitle>
              {pkg.bonus && (
                <p className="text-sm text-success font-medium">
                  +{pkg.bonus} bonus credits!
                </p>
              )}
            </CardHeader>
            <CardContent className="text-center">
              <div className="text-2xl font-bold text-foreground">${pkg.price}</div>
              <div className="text-sm text-muted-foreground">
                {pkg.bonus ? (
                  <>
                    <span className="line-through">${(pkg.credits * 0.1).toFixed(2)}</span>
                    <span className="ml-2 text-success">
                      ${((pkg.credits + pkg.bonus) * 0.1 / pkg.price * 10).toFixed(3)}/credit
                    </span>
                  </>
                ) : (
                  <span>${(pkg.price / pkg.credits).toFixed(3)}/credit</span>
                )}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                Total: {pkg.credits + (pkg.bonus || 0)} credits
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment Method Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 border border-dashed border-muted-foreground/30 rounded-lg text-center">
            <CreditCard className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Stripe Payment Integration Ready
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Demo mode - simulated payment processing with credit system integration
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Summary & Button */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-foreground">Purchase Summary</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>{selectedPackage.credits} credits</p>
                {selectedPackage.bonus && (
                  <p className="text-success">+{selectedPackage.bonus} bonus credits</p>
                )}
                <p className="font-medium">
                  Total: {selectedPackage.credits + (selectedPackage.bonus || 0)} credits
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-foreground">${selectedPackage.price}</div>
              <div className="text-sm text-muted-foreground">USD</div>
            </div>
          </div>

          <Button 
            onClick={handlePayment}
            disabled={isProcessing || isCreatingIntent || isProcessingPayment}
            className="w-full"
          >
            {isProcessing || isCreatingIntent || isProcessingPayment ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing Payment...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Purchase {selectedPackage.credits + (selectedPackage.bonus || 0)} Credits (Demo)
              </>
            )}
          </Button>

          <div className="mt-3 text-xs text-muted-foreground text-center">
            <p>Demo mode - simulated payment with full credit system integration</p>
            <p>Stripe Edge Functions ready for production use</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};